import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../config/config.json';
import {
  ItemTracker,
  CreateItemTrackerRequest,
  DeleteItemTrackerRequest,
  DeleteItemTrackerByItemRequest,
} from '../types/tracker';
import toast from 'react-hot-toast';

interface TrackedItemsState {
  items: ItemTracker[];
  isLoading: boolean;
  error: string | null;
}

const initialState: TrackedItemsState = {
  items: [],
  isLoading: false,
  error: null,
};

const trackedItemsSlice = createSlice({
  name: 'trackedItems',
  initialState,
  reducers: {
    setTrackedItems: (state, action: PayloadAction<ItemTracker[]>) => {
      state.items = action.payload;
    },
    addTrackedItem: (state, action: PayloadAction<ItemTracker>) => {
      state.items.push(action.payload);
    },
    removeTrackedItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
    },
    removeTrackedItemByItemId: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter((item) => Number(item.item_id) === action.payload);
    },
  },
});

export const { setTrackedItems, addTrackedItem, removeTrackedItem, removeTrackedItemByItemId } =
  trackedItemsSlice.actions;

// Helper function to transform response data
function transformTrackerData(data: any[]): ItemTracker[] {
  return (data || []).map((item: any) => ({
    id: item.id,
    user_id: item.user_id,
    item_id: item.item_id ? parseInt(item.item_id, 10) : undefined,
    product_id: item.product_id ? parseInt(item.product_id, 10) : undefined,
    skin_image_path: item.skin_image_path,
    image_path: item.image_path,
    name: item.name,
    shop_product_link: item.shop_product_link,
    created_at: item.created_at,
  }));
}

// Helper function to check if an item is tracked
export function isItemTracked(trackedItems: ItemTracker[], itemId: number): boolean {
  return trackedItems.some(
    (item) =>
      (item.item_id && Number(item.item_id) === Number(itemId)) ||
      (item.product_id && Number(item.product_id) === Number(itemId))
  );
}

// Tanstack Query Hooks
export function useTrackedItems() {
  const sessionToken = localStorage.getItem('session_token');

  return useQuery({
    queryKey: ['trackedItems'],
    queryFn: async () => {
      try {
        const response = await axios.get(`${config.backend_base_url}api/item/trackers`, {
          headers: { 'session-token': sessionToken },
        });

        if (!response.data) {
          return [];
        }

        const transformedData = transformTrackerData(response.data);
        return transformedData;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          toast.error('Failed to fetch tracked items');
        }
        throw error;
      }
    },
    initialData: [],
    enabled: !!sessionToken,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 2,
  });
}

export function useTracker(trackerId: number) {
  const sessionToken = localStorage.getItem('session_token');

  return useQuery({
    queryKey: ['tracker', trackerId],
    queryFn: async () => {
      const response = await axios.get(`${config.backend_base_url}api/item/tracker`, {
        headers: { 'session-token': sessionToken },
      });
      const data = response.data;
      return {
        ...data,
        item_id: parseInt(data.item_id, 10),
      } as ItemTracker;
    },
    enabled: !!sessionToken && !!trackerId,
  });
}

export function useCreateTracker() {
  const queryClient = useQueryClient();
  const sessionToken = localStorage.getItem('session_token');

  return useMutation({
    mutationFn: async (data: CreateItemTrackerRequest) => {
      const payload = {
        ...data,
        item_id: Number(data.item_id),
      };

      const response = await axios.post(`${config.backend_base_url}api/item/tracker`, payload, {
        headers: { 'session-token': sessionToken },
      });
      return {
        ...response.data,
        item_id: parseInt(response.data.item_id, 10),
      };
    },
    onSuccess: (data) => {
      // Update the tracked items cache
      queryClient.setQueryData<ItemTracker[]>(['trackedItems'], (oldData) => {
        const currentData = oldData || [];
        return [...currentData, data];
      });

      queryClient.invalidateQueries({ queryKey: ['trackedItems'] });
      toast.success('Item tracked successfully');
    },
    onError: () => {
      toast.error('Failed to track item');
    },
  });
}

export function useDeleteTracker() {
  const queryClient = useQueryClient();
  const sessionToken = localStorage.getItem('session_token');

  return useMutation({
    mutationFn: async (data: DeleteItemTrackerRequest) => {
      const response = await axios.delete(
        `${config.backend_base_url}api/item/tracker?id=${data.id}`,
        {
          headers: { 'session-token': sessionToken },
        }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Update the tracked items cache
      queryClient.setQueryData<ItemTracker[]>(['trackedItems'], (oldData) => {
        const currentData = oldData || [];
        return currentData.filter((item) => item.id !== variables.id);
      });

      queryClient.invalidateQueries({ queryKey: ['trackedItems'] });
      toast.success('Item untracked successfully');
    },
    onError: () => {
      toast.error('Failed to untrack item');
    },
  });
}

export function useDeleteTrackerByItem() {
  const queryClient = useQueryClient();
  const sessionToken = localStorage.getItem('session_token');

  return useMutation({
    mutationFn: async (data: DeleteItemTrackerByItemRequest) => {
      const response = await axios.delete(
        `${config.backend_base_url}api/item/tracker/by-item?item_id=${data.item_id}`,
        {
          headers: { 'session-token': sessionToken },
        }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Update the tracked items cache
      queryClient.setQueryData<ItemTracker[]>(['trackedItems'], (oldData) => {
        const currentData = oldData || [];
        return currentData.filter((item) => Number(item.item_id) !== Number(variables.item_id));
      });

      queryClient.invalidateQueries({ queryKey: ['trackedItems'] });
      toast.success('Item untracked successfully');
    },
    onError: () => {
      toast.error('Failed to untrack item');
    },
  });
}

export default trackedItemsSlice.reducer;
